export type Rarity = "common" | "uncommon" | "rare" | "epic" | "legendary";
export type Variant = "normal" | "shiny" | "rainbow" | "glitch";

export interface Character {
  id: number;
  name: string;
  nickname: string | null;
  rarity: Rarity;
  description: string | null;
  image_url: string | null;
}

export interface UserData {
  id: string;
  username: string;
  coins: number;
  click_power: number;
  team_size: number;
}

export interface CollectionCard {
  id: number;
  obtained_at: string;
  character_id: number;
  name: string;
  nickname: string | null;
  rarity: Rarity;
  description: string | null;
  image_url: string | null;
  variant: Variant;
  is_active: number;
}

export interface PackCard {
  character: Character;
  variant: Variant;
  isDuplicate: boolean;
  isUpgrade: boolean;
  coinsGained: number;
}

export type PackId = "standard" | "boosted" | "elite" | "mythic";
export type RarityWeights = Record<Rarity, number>;

export interface PackDefinition {
  id: PackId;
  name: string;
  description: string;
  cost: number;
  cardsPerPack: number;
  rarityWeights: RarityWeights;
}

export interface PackSummary {
  id: PackId;
  name: string;
  cost: number;
  cardsPerPack: number;
}

export interface PackResult {
  cards: PackCard[];
  coinsRemaining: number;
  pack: PackSummary;
}

export interface JobInfo {
  collectionId: number;
  characterId: number;
  name: string;
  nickname: string | null;
  image_url: string | null;
  rarity: Rarity;
  variant: Variant;
  jobName: string;
  company: string;
  baseRate: number;
  earningsPerTick: number;
  isActive: boolean;
}

export interface HustleState {
  clickPower: number;
  clickUpgradeCost: number;
  teamSize: number;
  teamUpgradeCost: number;
  activeTeam: JobInfo[];
  bench: JobInfo[];
  coinsPerTick: number;
}

export interface CollectionResponse {
  cards: CollectionCard[];
  stats: {
    totalCards: number;
    uniqueCards: number;
    totalCharacters: number;
  };
}

export const RARITY_ORDER: Rarity[] = [
  "legendary",
  "epic",
  "rare",
  "uncommon",
  "common",
];

export const DEFAULT_PACK_ID: PackId = "standard";
export const PACK_DEFINITIONS: PackDefinition[] = [
  {
    id: "standard",
    name: "Standard Pack",
    description: "Balanced odds. Great for steady collection progress.",
    cost: 220,
    cardsPerPack: 5,
    rarityWeights: {
      common: 50,
      uncommon: 25,
      rare: 15,
      epic: 8,
      legendary: 2,
    },
  },
  {
    id: "boosted",
    name: "Boosted Pack",
    description: "Fewer commons and better odds for rare+ pulls.",
    cost: 640,
    cardsPerPack: 5,
    rarityWeights: {
      common: 30,
      uncommon: 28,
      rare: 22,
      epic: 14,
      legendary: 6,
    },
  },
  {
    id: "elite",
    name: "Elite Pack",
    description: "Top-shelf odds with a serious shot at epic and legendary cards.",
    cost: 1850,
    cardsPerPack: 5,
    rarityWeights: {
      common: 15,
      uncommon: 25,
      rare: 28,
      epic: 20,
      legendary: 12,
    },
  },
  {
    id: "mythic",
    name: "Mythic Pack",
    description: "Only epic and legendary pulls. Zero lower rarities.",
    cost: 5200,
    cardsPerPack: 5,
    rarityWeights: {
      common: 0,
      uncommon: 0,
      rare: 0,
      epic: 70,
      legendary: 30,
    },
  },
];

export const PACK_BY_ID = PACK_DEFINITIONS.reduce(
  (acc, pack) => {
    acc[pack.id] = pack;
    return acc;
  },
  {} as Record<PackId, PackDefinition>
);

export const VARIANT_WEIGHTS: Record<Variant, number> = {
  normal: 85,
  shiny: 10,
  rainbow: 4,
  glitch: 1,
};

export const VARIANT_MULTIPLIER: Record<Variant, number> = {
  normal: 1,
  shiny: 1.5,
  rainbow: 2,
  glitch: 3,
};

export const SELL_VALUES: Record<Rarity, number> = {
  common: 2,
  uncommon: 7,
  rare: 20,
  epic: 60,
  legendary: 180,
};

export const BASE_RATES: Record<Rarity, number> = {
  common: 1,
  uncommon: 2,
  rare: 4,
  epic: 10,
  legendary: 24,
};

export const TICK_INTERVAL_MS = 10_000;
export const MAX_OFFLINE_TICKS = 360;

export const VARIANT_ORDER: Variant[] = ["normal", "shiny", "rainbow", "glitch"];

export function getClickUpgradeCost(currentPower: number): number {
  return Math.floor(180 * Math.pow(1.85, currentPower - 1));
}

export function getTeamUpgradeCost(currentSize: number): number {
  return Math.floor(600 * Math.pow(2.4, currentSize - 3));
}
